'use client';

import Link from 'next/link';
import { ArrowRight, ChevronDown, FolderOpen } from 'lucide-react';
import { Tool } from '@/types/tools.types';
import { useToolsDocsStore } from '@/stores/tools-docs-store';
import { cn } from '@/utils/utils';

type DocumentationModuleProps = {
	docs: Tool[];
};

export function DocumentationModule({ docs }: DocumentationModuleProps): JSX.Element {
	const isExpanded = useToolsDocsStore((state) => state.isExpanded);
	const toggle = useToolsDocsStore((state) => state.toggle);

	return (
		<article
			className={cn(
				'space-card flex flex-col rounded-xl transition-colors',
				isExpanded ? 'md:col-span-2' : 'hover:border-orange-500/30',
			)}
		>
			<button
				type="button"
				onClick={toggle}
				aria-expanded={isExpanded}
				className="flex w-full items-start gap-4 p-6 text-left"
			>
				<span className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-orange-500/10 text-orange-500">
					<FolderOpen className="h-5 w-5" />
				</span>
				<span className="flex flex-grow flex-col">
					<span className="mb-2 flex items-center justify-between gap-3">
						<span className="text-xs font-medium uppercase tracking-wide text-orange-500">
							Documentation
						</span>
						<span className="rounded-full bg-white/5 px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
							{docs.length} templates
						</span>
					</span>
					<span className="mb-2 text-xl font-bold font-space-grotesk">
						Engagement Document Library
					</span>
					<span className="text-muted-foreground">
						Fillable, client-ready templates — discovery through delivery. Every one exports a
						branded PDF.
					</span>
				</span>
				<ChevronDown
					className={cn(
						'mt-1 h-5 w-5 flex-none text-muted-foreground transition-transform duration-200',
						isExpanded && 'rotate-180',
					)}
				/>
			</button>

			{isExpanded ? (
				<div className="grid gap-2 border-t border-white/10 p-4 sm:grid-cols-2">
					{docs.map((doc) => (
						<Link
							key={doc.slug}
							href={doc.assetPath ?? '#'}
							className="group flex items-center justify-between gap-3 rounded-lg px-3 py-3 text-sm transition-colors hover:bg-white/5"
						>
							<span>
								<span className="block font-medium text-white">{doc.name}</span>
								<span className="mt-0.5 block text-xs text-muted-foreground">
									{doc.description}
								</span>
							</span>
							<ArrowRight className="h-4 w-4 flex-none text-orange-500 opacity-0 transition-opacity group-hover:opacity-100" />
						</Link>
					))}
				</div>
			) : null}
		</article>
	);
}
